import { useCallback, useEffect, useState, type FormEvent } from "react";

import { useAuth } from "../auth/AuthContext";
import { formatDateTime } from "../lib/format";
import {
  fetchLoginAttempts,
  OUTCOME_META,
  REASON_LABELS,
  reasonLabel,
  type LoginAttempt,
  type LoginAttemptFilter,
  type LoginFailureReason,
  type LoginOutcome,
} from "../lib/loginAudit";

/**
 * Экран «Журнал входов» (D-2, аудит входов §7/§10 ТЗ). Доступ — юрист/админ (право
 * `login_audit:view`; сервер авторитетен, правило 5). Фильтры: логин (подстрока), итог, причина,
 * период (в поясе Омска, Р-16), пагинация. Пароль в журнале не хранится и не показывается.
 */

const PAGE_SIZE = 50;

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[12px] text-muted";

interface Draft {
  login: string;
  outcome: "" | LoginOutcome;
  reason: "" | LoginFailureReason;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_DRAFT: Draft = { login: "", outcome: "", reason: "", dateFrom: "", dateTo: "" };

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly items: LoginAttempt[]; readonly total: number }
  | { readonly kind: "error"; readonly message: string };

export function LoginAttemptsPage() {
  const { logout } = useAuth();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [filter, setFilter] = useState<LoginAttemptFilter>({ page: 1, limit: PAGE_SIZE });
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchLoginAttempts(filter);
    if (res.ok) {
      setState({ kind: "ready", items: res.value.items, total: res.value.total });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [filter, logout]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    setFilter({
      login: draft.login.trim() || undefined,
      outcome: draft.outcome || undefined,
      reason: draft.reason || undefined,
      dateFrom: draft.dateFrom || undefined,
      dateTo: draft.dateTo || undefined,
      page: 1,
      limit: PAGE_SIZE,
    });
  };

  const reset = () => {
    setDraft(EMPTY_DRAFT);
    setFilter({ page: 1, limit: PAGE_SIZE });
  };

  const total = state.kind === "ready" ? state.total : 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goPage = (page: number) => setFilter((f) => ({ ...f, page }));

  return (
    <section aria-labelledby="login-audit-title">
      <h1 id="login-audit-title" className="text-[20px] font-bold text-ink">
        Журнал входов
      </h1>
      <p className="mt-1 max-w-[var(--w-text)] text-[13px] text-muted">
        Попытки входа в систему: время (пояс Омска), логин, IP, итог и причина отказа. Пароль не
        сохраняется. Причина отказа видна здесь для разбора, но самому входящему не раскрывается.
      </p>

      <form
        onSubmit={apply}
        className="mt-4 grid gap-3 rounded-[var(--radius-l)] border border-border bg-surface p-4 sm:grid-cols-2 lg:grid-cols-3"
        aria-label="Фильтр журнала входов"
      >
        <div>
          <label htmlFor="f-login" className={labelCls}>
            Логин (подстрока)
          </label>
          <input
            id="f-login"
            value={draft.login}
            onChange={(e) => setDraft((d) => ({ ...d, login: e.target.value }))}
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="f-outcome" className={labelCls}>
            Итог
          </label>
          <select
            id="f-outcome"
            value={draft.outcome}
            onChange={(e) =>
              setDraft((d) => ({ ...d, outcome: e.target.value as Draft["outcome"] }))
            }
            className={inputCls}
          >
            <option value="">Любой</option>
            <option value="success">{OUTCOME_META.success.label}</option>
            <option value="failure">{OUTCOME_META.failure.label}</option>
          </select>
        </div>
        <div>
          <label htmlFor="f-reason" className={labelCls}>
            Причина отказа
          </label>
          <select
            id="f-reason"
            value={draft.reason}
            onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value as Draft["reason"] }))}
            className={inputCls}
          >
            <option value="">Любая</option>
            {(Object.keys(REASON_LABELS) as LoginFailureReason[]).map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="f-from" className={labelCls}>
            Период с
          </label>
          <input
            id="f-from"
            type="date"
            value={draft.dateFrom}
            onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="f-to" className={labelCls}>
            по
          </label>
          <input
            id="f-to"
            type="date"
            value={draft.dateTo}
            onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
            className={`num ${inputCls}`}
          />
        </div>
        <div className="flex items-end gap-2">
          <button type="submit" className={btnPrimary}>
            Применить
          </button>
          <button type="button" onClick={reset} className={btnSecondary}>
            Сбросить
          </button>
        </div>
      </form>

      <div className="mt-4">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка журнала…
          </p>
        )}
        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-5"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}
        {state.kind === "ready" &&
          (state.items.length === 0 ? (
            <p className="text-[13px] text-muted">Записей по заданному фильтру нет.</p>
          ) : (
            <>
              <div className="min-w-fit rounded-[var(--radius-l)] border border-border bg-surface">
                <table className="sticky-head w-full border-collapse text-[14px]">
                  <caption className="sr-only">Журнал попыток входа</caption>
                  <thead>
                    <tr className="border-b border-border text-left text-[12px] text-muted">
                      <th scope="col" className="px-4 py-2.5 font-bold">
                        Время (Омск)
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-bold">
                        Логин
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-bold">
                        IP
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-bold">
                        Итог
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-bold">
                        Причина
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.items.map((a) => (
                      <tr key={a.id} className="border-b border-border-soft last:border-b-0">
                        <td className="num px-4 py-2.5 whitespace-nowrap text-ink">
                          {formatDateTime(a.at)}
                        </td>
                        <td className="num px-4 py-2.5 whitespace-nowrap text-ink">{a.login}</td>
                        <td className="num px-4 py-2.5 whitespace-nowrap text-muted">
                          {a.ip ?? "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`stamp ${OUTCOME_META[a.outcome].cls}`}>
                            {OUTCOME_META[a.outcome].label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-muted">{reasonLabel(a.reason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[13px] text-muted">
                <span>Всего записей: {total}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={filter.page <= 1}
                    onClick={() => goPage(filter.page - 1)}
                    className={btnSecondary}
                  >
                    ← Назад
                  </button>
                  <span>
                    Стр. {filter.page} из {pageCount}
                  </span>
                  <button
                    type="button"
                    disabled={filter.page >= pageCount}
                    onClick={() => goPage(filter.page + 1)}
                    className={btnSecondary}
                  >
                    Вперёд →
                  </button>
                </div>
              </div>
            </>
          ))}
      </div>
    </section>
  );
}
